import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import { HeaderWrapper } from '@seedlang/hoc';
import { Link } from 'react-router';
import Dropdown from 'components/dropdown';
import { CloseIcon, CrownIcon, MenuIcon } from '@seedlang/icons';
import { AppUI } from '@seedlang/state';
import UserProfileImage from 'components/user/user_profile_image';
import WhitelabelLogo from 'components/whitelabel_logo';
import styled from '@emotion/styled';
import GoProButton from 'components/button/go_pro_button';
import { Theme } from '@seedlang/constants';
import HeaderButton from 'components/button/header_button';
import NavigationItemLinks from 'components/layout/desktop/navigation_item_links';
import autobind from 'autobind-decorator';
import NotificationsWithPopup from 'components/notification/notifications_with_popup';
import { isPresent, pixify } from '@seedlang/utils';

const AgreementWrapper = styled.div`
  width: 100%;
`;

const Wrapper = styled.header`
  display: flex;
  z-index: 999;
  width: 100%;
  height: ${pixify(AppUI.layout.desktopHeaderHeight)};
  min-height: ${pixify(AppUI.layout.desktopHeaderHeight)};
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background: ${props => props.background};
  position: ${props => props.position};
  border-bottom: ${props => props.borderBottom};
  top: 0;
  .on-click {
    cursor: pointer;
  }
  .on-click:hover {
    color: #FFF;
  }
`;

const MembershipBanner = styled.div`
  background: ${props => props.background};
  height: 30px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFF;
  font-size: 16px;
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #FFF;
  }
  .icon-wrapper {
    margin-right: 5px;
  }
`;

const InnerWrapper = styled.div`
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1180px;
  position: relative;
`;

const LogoWrapper = styled.div`
  width: 190px;
  cursor: pointer;
`;

const LinkWrapper = styled.button`
  appearance: none;
  border: none;
  background: none;
  display: ${props => props.display};
  padding-bottom: 3px;
  margin-left: 15px;
  font-size: 17px;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-color: ${props => props.borderColor};
  &:hover {
    color: #000!important;
  }
`;

const NavigationButtons = styled.nav`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const MobileNavigation = styled.div`
  position: absolute;
  width: 100%;
  background: #FFF;
  top: ${props => props.top};
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 20px 30px 20px 0;
  z-index: 99;
  border-bottom: 1px solid #b7b7b7;
`;

const MobileIconWrapper = styled.button`
  appearance: none;
  border: none;
  background: none;
  display: flex;
  width: 50px;
  margin-left: 10px;
  .menu-icon, .close-icon {
    cursor: pointer;
    svg {
      fill: #000;
      width: 25px;
    }
  }
`;

const Hr = styled.div`
  width: 140px;
  border-bottom: 1px solid #b7b7b7;
  height: 5px;
  margin: 10px 0;
`;

@observer
class Header extends React.Component {
  @observable showMobileNavigation = false;

  static defaultProps = {
    background: Theme.siteHeaderColor,
  };

  @autobind signOutAndHideNavigation() {
    this.showMobileNavigation = false;
    AppUI.signOut(AppUI.siteIsDefault);
  }

  @computed get mobileView() {
    return AppUI.layout.viewportWidth < 800;
  }

  @computed get showNavigationItems() {
    return !this.mobileView && !this.props.simplifiedHeader && this.props.navigationItems && !!this.props.navigationItems.length;
  }

  render() {
    return (
      <AgreementWrapper>
        {
          this.props.showWhiteLabelMembershipButton && AppUI.layout.isMobile &&
            <MembershipBanner
              background={AppUI.site.accentColor || '#1f4d6b'}
            >
              <Link to='memberships.index'>
                <CrownIcon />
                <span>Become a Member</span>
              </Link>
            </MembershipBanner>
        }
        <Wrapper
          background={this.props.background}
          position={this.props.position}
        >
          {
            this.showMobileNavigation && this.props.navigationItems &&
              <MobileNavigation
                top={this.props.showWhiteLabelMembershipButton ? '90px' : '70px'}
                id='mobile-navigation'
              >
                {
                  !AppUI.userIsSignedInAndNotGuest &&
                    <Link to='onboarding.sign_in'>
                      <LinkWrapper
                        borderColor='#FFF'
                      >
                        Sign In
                      </LinkWrapper>
                    </Link>
                }
                {
                  !AppUI.userIsSignedInAndNotGuest &&
                    <Hr />
                }
                <NavigationItemLinks
                  {...this.props}
                  header
                  flexDirection='column'
                  alignItems='flex-end'
                  afterClick={() => this.showMobileNavigation = false}
                />
                {
                  AppUI.userIsSignedInAndNotGuest &&
                    <Hr />
                }
                {
                  AppUI.userIsSignedInAndNotGuest &&
                    <LinkWrapper
                      borderColor={AppUI.routeStore.routeName.startsWith('settings.all') ? '#b7b7b7' : 'transparent'}
                    >
                      <Link
                        to='settings.all'
                        onClick={() => this.showMobileNavigation = false}
                      >
                        Settings
                      </Link>
                    </LinkWrapper>
                }
                {
                  AppUI.userIsSignedInAndNotGuest &&
                    <LinkWrapper
                      as='button'
                      type='button'
                      borderColor='#FFF'
                      onClick={this.signOutAndHideNavigation}
                      className='on-click'
                    >
                      Sign Out
                    </LinkWrapper>
                }
              </MobileNavigation>
            }
          <InnerWrapper>
            {
              AppUI.site?.image &&
                <LogoWrapper>
                  <Link to={this.props.whiteLabelHomeRoute}>
                    <WhitelabelLogo />
                  </Link>
                </LogoWrapper>
            }
            <NavigationButtons>
              {
                this.showNavigationItems &&
                  <NavigationItemLinks
                    {...this.props}
                    header
                    matchRoute={this.matchRoute}
                    flexDirection='row'
                  />
              }
              {
                this.props.showWhiteLabelMembershipButton && !AppUI.layout.isMobile &&
                  <Link to='memberships.index'>
                    <GoProButton
                      marginLeft='15px'
                      height='47px'
                      background={AppUI.user?.site?.accentColor}
                      disabled={isPresent(AppUI.routeStore.routeName) && AppUI.routeStore.routeName === 'memberships.index'}
                    >
                      <div className='text'>
                        <span>Become a Member</span>
                      </div>
                    </GoProButton>
                  </Link>
              }
              {
                !this.mobileView && (!AppUI.userIsSignedIn || AppUI.user.guest) &&
                  <Link
                    to='onboarding.sign_in'
                  >
                    <HeaderButton
                      height='47px'
                      borderRadius='47px'
                      fontWeight='bold'
                      background={AppUI.user?.site?.accentColor}
                    >
                      Sign In
                    </HeaderButton>
                  </Link>
              }
              {
                this.mobileView &&
                  <MobileIconWrapper
                    type='button'
                    aria-label={
                      this.showMobileNavigation
                        ? 'Close navigation menu' : 'Open navigation menu'
                    }
                    aria-controls='mobile-navigation'
                    aria-expanded={this.showMobileNavigation}
                    onClick={() => this.showMobileNavigation = !this.showMobileNavigation}
                  >
                    {
                      !this.showMobileNavigation &&
                        <MenuIcon />
                    }
                    {
                      this.showMobileNavigation &&
                        <CloseIcon />
                    }
                  </MobileIconWrapper>
              }
              {
                !AppUI.user?.guest && AppUI.userIsSignedIn && !this.mobileView &&
                  <NotificationsWithPopup
                    background='#efefef'
                  />
              }
              {
                !AppUI.user?.guest && !this.props.simplifiedHeader && AppUI.userIsSignedIn && !this.mobileView &&
                  <Dropdown
                    className='user-profile user-image'
                    top='60px'
                    right={0}
                    backgroundOnHover='gray'
                  >
                    <UserProfileImage
                      hideProStatus
                      hideTooltip
                      margin='0 0 0 15px'
                      width={42}
                      user={AppUI.user}
                    />
                    <ul>
                      <li>
                        <Link
                          to='settings.user_profile'
                        >
                          Settings
                        </Link>
                      </li>
                      {
                        AppUI.user.isMember && (
                          <li>
                            <Link to='memberships.change'>
                              My Membership
                            </Link>
                          </li>
                        )
                      }
                      {
                        AppUI.user.currentUserGroup?.admin &&
                            <li>
                              <a href='/creator/posts'>
                                Admin
                              </a>
                            </li>
                      }
                      <li
                        onClick={() => AppUI.signOut(AppUI.siteIsDefault)}
                        className='on-click'
                      >
                        Sign Out
                      </li>
                    </ul>
                  </Dropdown>
              }
            </NavigationButtons>
          </InnerWrapper>
        </Wrapper>
      </AgreementWrapper>
    );
  }
}

export default HeaderWrapper(Header);
